import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { WarningIcon as Warning } from "@phosphor-icons/react";
import { useGeneralAck } from "@/components/BuyerNoticeSheet";

/**
 * Inline "Quick checklist" gate rendered directly above Buy / Inquire /
 * Offer action buttons. Two modes:
 *
 *   1. Restricted-breed listing (server returns `breed_warning` on the
 *      listing payload, e.g. "Pit Bull"): always shows the gate with a
 *      breed-specific warning and an "I have verified this is legal in
 *      my jurisdiction" checkbox. The action button below stays
 *      disabled until the box is ticked. Acknowledgment is recorded
 *      per-listing in localStorage AND surfaced to the parent via
 *      `onConsentChange(boolean)` so the parent can disable / enable
 *      its primary action.
 *
 *   2. Unrestricted listing: if the user has already ticked the
 *      session-wide "I've read the buyer checklist" box once, the gate
 *      auto-acknowledges and renders nothing. First time only, it shows
 *      a subtle one-line "By inquiring you agree…" hint with a single
 *      acknowledgment checkbox. Once ticked, the user never sees the
 *      gate on unrestricted listings again.
 *
 * Props:
 *   listing            — the marketplace listing / dog row. Looks at
 *                        `breed_warning` (server-supplied string when
 *                        the breed is restricted) and `breed`.
 *   actionLabel        — what the consumer's button is called, used in
 *                        the gate copy (e.g. "Buy now", "Inquire",
 *                        "Make an offer"). Default: "continue".
 *   onConsentChange    — fn(boolean) called when consent state flips so
 *                        the parent can enable / disable its CTA.
 *   testId             — scope test ids to a unique listing row.
 *
 * Consent persistence shape:
 *   localStorage["buyer_listing_ack"] = JSON.stringify({ <listingId>: ts })
 *   localStorage["buyer_general_ack"] = "1" once user ticks the
 *      session-wide acknowledgment.
 */
const LISTING_ACK_KEY = "buyer_listing_ack";

function readListingAcks() {
  try { return JSON.parse(localStorage.getItem(LISTING_ACK_KEY) || "{}"); } catch { return {}; }
}
function persistListingAck(id) {
  try {
    const acks = readListingAcks();
    acks[id] = new Date().toISOString();
    localStorage.setItem(LISTING_ACK_KEY, JSON.stringify(acks));
  } catch { /* noop */ }
}
function isListingAcked(id) {
  if (!id) return false;
  const acks = readListingAcks();
  return Boolean(acks[id]);
}

/**
 * Hook used by parent action buttons to read whether buyer consent is
 * currently satisfied for the given listing. Mirrors the same logic
 * the gate component uses, so a parent can short-circuit a button
 * without rendering the gate at all (handy for the BuyNow card variant
 * where space is tight).
 */
export function useBuyerConsent(listing) {
  const [generalAck] = useGeneralAck();
  const [listingAck, setListingAck] = useState(isListingAcked(listing?.id));
  useEffect(() => { setListingAck(isListingAcked(listing?.id)); }, [listing?.id]);
  const isRestricted = Boolean(listing?.breed_warning);
  const ok = isRestricted ? listingAck : generalAck || listingAck;
  return {
    ok,
    isRestricted,
    refresh: () => setListingAck(isListingAcked(listing?.id)),
  };
}

export default function BuyerConsentGate({
  listing,
  actionLabel = "continue",
  onConsentChange,
  testId = "consent-gate",
}) {
  const [generalAck, setGeneralAck] = useGeneralAck();
  const [listingAck, setListingAck] = useState(isListingAcked(listing?.id));
  const isRestricted = Boolean(listing?.breed_warning);
  const id = listing?.id;

  // Notify parent of state on mount + every change.
  useEffect(() => {
    const ok = isRestricted ? listingAck : generalAck || listingAck;
    onConsentChange?.(ok);
  }, [isRestricted, generalAck, listingAck, onConsentChange]);

  // Auto-record acknowledgment server-side so it ends up on the
  // inquiry / offer / escrow audit row when the user actually
  // proceeds. Best-effort; failure is non-blocking.
  const persistOnAck = async () => {
    if (!id) return;
    persistListingAck(id);
    setListingAck(true);
    try {
      await api.post(`/marketplace/listings/${id}/consent`, {
        acknowledged: true,
        breed_warning: listing?.breed_warning || null,
        breed: listing?.breed || null,
      });
    } catch {
      /* server log may not be available; localStorage is authoritative
         for the UI gate. The action's own POST will also include
         buyer_consent={acknowledged:true,…} when supported. */
    }
  };

  // ----- Restricted breed: per-listing gate -----
  if (isRestricted) {
    if (listingAck) {
      return (
        <div data-testid={`${testId}-acked`} className="text-[11px] text-[#0F8043] flex items-center gap-1 mt-1">
          <span>✓</span>
          <span>Acknowledged · Restricted breed.</span>
          <button
            type="button"
            onClick={() => {
              try {
                const acks = readListingAcks();
                delete acks[id];
                localStorage.setItem(LISTING_ACK_KEY, JSON.stringify(acks));
              } catch { /* noop */ }
              setListingAck(false);
            }}
            className="ml-1 underline text-[#6B6B6B] hover:text-ink"
            data-testid={`${testId}-undo`}
          >
            Undo
          </button>
        </div>
      );
    }
    return (
      <div
        data-testid={`${testId}-restricted`}
        className="rounded-lg border border-[#C1272D]/40 bg-[#FCE9EA]/50 p-3 text-[12px] text-[#7A1014] mt-2"
      >
        <div className="flex items-start gap-2">
          <Warning size={14} weight="fill" className="text-crimson mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold leading-tight">Restricted breed</p>
            <p className="mt-0.5 leading-snug">
              {listing.breed_warning}
            </p>
            <label className="flex items-start gap-2 mt-2 cursor-pointer text-ink">
              <Checkbox
                checked={listingAck}
                onCheckedChange={(v) => v && persistOnAck()}
                className="mt-0.5"
                data-testid={`${testId}-restricted-checkbox`}
              />
              <span>I have verified that ownership of this breed is legal in my jurisdiction.</span>
            </label>
          </div>
        </div>
      </div>
    );
  }

  // ----- Unrestricted breed: one-time soft gate -----
  if (generalAck) return null;

  return (
    <label
      data-testid={`${testId}-general`}
      className="flex items-start gap-2 mt-2 text-[11px] text-[#6B6B6B] cursor-pointer leading-snug"
    >
      <Checkbox
        checked={generalAck}
        onCheckedChange={(v) => {
          if (v) {
            setGeneralAck(true);
            persistOnAck();
          }
        }}
        className="mt-0.5"
        data-testid={`${testId}-general-checkbox`}
      />
      <span>
        By choosing to {actionLabel} I agree to the{" "}
        <a href="/terms" className="underline">Terms</a> and have read the{" "}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            // Trigger the pill in the header to open. We piggy-back on
            // a custom event so we don't need to thread refs across the
            // component tree.
            window.dispatchEvent(new CustomEvent("open-buyer-notice"));
          }}
          className="underline text-ink hover:text-crimson"
          data-testid={`${testId}-open-checklist`}
        >
          buyer checklist
        </button>.
      </span>
    </label>
  );
}
